import React, { useState, useEffect, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, Mail, Send } from 'react-feather';
import VisitorLayout from '../../components/layouts/VisitorLayout';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Alert from '../../components/common/Alert';
import CoupleService from '../../services/couple.service';
import GuestService, { RSVPStatus, MealChoice } from '../../services/guest.service';
import { Couple } from '../../types';

const RSVP: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const [couple, setCouple] = useState<Couple | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Search mode
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchEmail, setSearchEmail] = useState<string>('');

  // RSVP form states
  const [step, setStep] = useState<'search' | 'form' | 'success'>('search');
  const [foundGuest, setFoundGuest] = useState<any | null>(null);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [attending, setAttending] = useState<boolean | null>(null);
  const [numberOfGuests, setNumberOfGuests] = useState<number>(1);
  const [mealPreference, setMealPreference] = useState<string>(MealChoice.NOT_SELECTED);
  const [message, setMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!customUrl) return;

      try {
        setLoading(true);
        const coupleData = await CoupleService.getCoupleByCustomUrl(customUrl);
        setCouple(coupleData);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load wedding details';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customUrl]);

  const handleSearch = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!couple) return;

    setIsSearching(true);
    setError(null);

    try {
      const result = await GuestService.findGuestByEmail(searchEmail, couple._id);

      if (result.found && result.guest) {
        // Found an existing invitation
        setFoundGuest(result.guest);
        setName(result.guest.name || '');
        setEmail(result.guest.email || searchEmail);
        setAttending(
          result.guest.rsvpStatus === RSVPStatus.ATTENDING ? true :
          result.guest.rsvpStatus === RSVPStatus.DECLINED ? false :
          null
        );
        setNumberOfGuests(result.guest.plusOneAllowed ? 2 : 1);
        setMealPreference(result.guest.specialMealRequest || MealChoice.NOT_SELECTED);
        setMessage(result.guest.notes || '');
        setStep('form');
      } else {
        // No invitation found, allow RSVP
        setFoundGuest(null);
        setName('');
        setEmail(searchEmail);
        setAttending(null);
        setNumberOfGuests(1);
        setMealPreference(MealChoice.NOT_SELECTED);
        setMessage('');
        setStep('form');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search for invitation';
      setError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmitRSVP = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!couple) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Split name into first and last name
      const nameParts = name.split(' ');
      const firstName = nameParts[0];
      // Use "Guest" as fallback lastName if none provided
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Guest';

      // Use the new backend format
      const rsvpData = {
        firstName,
        lastName,
        email,
        attendanceStatus: attending === true ? RSVPStatus.ATTENDING :
                         attending === false ? RSVPStatus.DECLINED :
                         RSVPStatus.PENDING,
        mealChoice: mealPreference || MealChoice.NOT_SELECTED,
        notes: message || undefined,
        hasPlusOne: attending === true && numberOfGuests > 1,
        plusOneAllowed: attending === true && numberOfGuests > 1,
        couple: couple._id,
      };

      const result = await GuestService.submitRSVP(rsvpData);

      setSuccessMessage(
        attending === true
          ? 'Thank you for confirming your attendance! You will receive a confirmation email shortly.'
          : attending === false
          ? "Thank you for letting us know. We'll miss you at our celebration!"
          : 'Thank you for your response! You will receive a confirmation email shortly.'
      );

      setStep('success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit RSVP';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFoundGuest(null);
    setName('');
    setEmail('');
    setAttending(null);
    setNumberOfGuests(1);
    setMealPreference(MealChoice.NOT_SELECTED);
    setMessage('');
    setSearchEmail('');
    setError(null);
    setSuccessMessage(null);
    setStep('search');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size="lg" label="Loading wedding details..." />
      </div>
    );
  }

  if (error && !couple) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-pink-500" />
          <h2 className="mt-2 text-lg font-medium text-gray-900">Wedding Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">
            {error || "We couldn't find the wedding you're looking for."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <VisitorLayout couple={couple}>
      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">RSVP</h2>
            <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-500 sm:mt-4">
              We're excited to celebrate with you! Please let us know if you'll be joining us.
            </p>
          </div>

          <div className="mt-12 max-w-lg mx-auto bg-white shadow-sm rounded-lg overflow-hidden">
            {error && (
              <Alert
                type="error"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
                className="mb-4"
              />
            )}

            {step === 'search' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Find Your Invitation</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Enter your email to find your invitation. If you don't have an invitation yet, you can still RSVP.
                </p>

                <form onSubmit={handleSearch} className="space-y-4">
                  <Input
                    id="search-email"
                    label="Email Address"
                    type="email"
                    required
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="Enter your email"
                  />

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      loading={isSearching}
                      disabled={isSearching || !searchEmail}
                      leftIcon={<Mail className="h-4 w-4" />}
                    >
                      Find Invitation
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {step === 'form' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {foundGuest ? 'Update Your RSVP' : 'Send Your RSVP'}
                </h3>
                <form onSubmit={handleSubmitRSVP} className="space-y-6">
                  <div className="space-y-4">
                    <Input
                      id="rsvp-name"
                      label="Your Name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                    />

                    <Input
                      id="rsvp-email"
                      label="Email Address"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      disabled={!!foundGuest}
                    />

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Will you be attending?
                      </label>
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          className={`px-4 py-2 rounded-md flex items-center space-x-2 border ${
                            attending === true
                              ? 'bg-green-100 border-green-500 text-green-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => setAttending(true)}
                        >
                          <Check className="h-5 w-5" />
                          <span>Yes, I'll be there</span>
                        </button>
                        <button
                          type="button"
                          className={`px-4 py-2 rounded-md flex items-center space-x-2 border ${
                            attending === false
                              ? 'bg-red-100 border-red-500 text-red-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => setAttending(false)}
                        >
                          <X className="h-5 w-5" />
                          <span>No, I can't make it</span>
                        </button>
                      </div>
                    </div>

                    {attending === true && (
                      <>
                        <div>
                          <label htmlFor="number-of-guests" className="block text-sm font-medium text-gray-700">
                            Number of Guests (including yourself)
                          </label>
                          <select
                            id="number-of-guests"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md text-gray-900"
                            value={numberOfGuests}
                            onChange={(e) => setNumberOfGuests(parseInt(e.target.value, 10))}
                          >
                            <option value={1}>Just me</option>
                            <option value={2}>2 (Me + Guest)</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="meal-preference" className="block text-sm font-medium text-gray-700">
                            Meal Preference
                          </label>
                          <select
                            id="meal-preference"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md text-gray-900"
                            value={mealPreference}
                            onChange={(e) => setMealPreference(e.target.value)}
                          >
                            <option value={MealChoice.NOT_SELECTED}>No Preference</option>
                            <option value={MealChoice.VEGETARIAN}>Vegetarian</option>
                            <option value={MealChoice.VEGAN}>Vegan</option>
                            <option value={MealChoice.FISH}>Fish</option>
                            <option value={MealChoice.CHICKEN}>Chicken</option>
                            <option value={MealChoice.BEEF}>Beef</option>
                            <option value={MealChoice.SPECIAL_REQUEST}>Special Request</option>
                          </select>
                        </div>
                      </>
                    )}

                    <Textarea
                      id="rsvp-message"
                      label="Message (optional)"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a message for the couple"
                    />
                  </div>

                  <div className="flex justify-between">
                    <Button variant="outline" onClick={resetForm}>
                      Back
                    </Button>
                    <Button
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting || !name || !email || attending === null}
                      leftIcon={<Send className="h-4 w-4" />}
                    >
                      Submit RSVP
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {step === 'success' && (
              <div className="p-6 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="mt-3 text-lg font-medium text-gray-900">RSVP Submitted!</h3>
                <p className="mt-2 text-sm text-gray-500">{successMessage}</p>
                <div className="mt-6">
                  <Button onClick={resetForm} variant="outline">
                    Submit Another Response
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </VisitorLayout>
  );
};

export default RSVP;